// frontend/src/pages/ReferralLanding.js
//
// Where a partner's link and QR code land: /r/SALON-K7X2.
//
// Deliberately OUTSIDE the /el|/en prefix every other page lives under. This URL
// gets printed onto a mirror sticker and a business card, and printed things
// cannot be re-issued when a visitor turns out to read the other language — so
// the poster carries one language-neutral address and the reader is sent into
// their own language from here.
//
// It also does the one job the address bar cannot: it turns a scan into a stored
// referral before the visitor has done anything at all, so the salon still gets
// credit if the account is created three weeks later on a different tab.

import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useNavigate } from '../routing';
import { useTranslation } from 'react-i18next';
import { Sparkles, ArrowRight } from 'lucide-react';
import api from '../utils/api';
import { rememberReferral } from '../utils/referral';
import theme from '../theme';

export default function ReferralLanding() {
  const { code } = useParams();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [offer, setOffer] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Remembered BEFORE the lookup and regardless of what it says. The lookup is
    // for what to put on the screen; the attribution must survive an offline
    // moment, a slow response, or a visitor who taps through in half a second.
    rememberReferral(code, { path: `/r/${code}` });

    let cancelled = false;
    api.get(`/affiliates/offer/${encodeURIComponent(code)}`)
      .then((data) => { if (!cancelled) setOffer(data); })
      // An unknown code is not an error the visitor should see. They followed a
      // link a salon gave them; being told it is invalid reflects on the salon
      // and on us, and there is nothing they can do about it either way. Send
      // them to the catalogue as if they had arrived normally.
      .catch(() => { if (!cancelled) setOffer({ valid: false }); })
      .finally(() => { if (!cancelled) setLoading(false); });

    return () => { cancelled = true; };
  }, [code]);

  useEffect(() => {
    if (!loading && offer && !offer.valid) navigate('/browse', { replace: true });
  }, [loading, offer, navigate]);

  if (loading || !offer?.valid) {
    return (
      <div style={{
        minHeight: '100vh', background: theme.inkDeep, color: theme.gold,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        fontFamily: theme.font.display, fontSize: 24,
      }}>…</div>
    );
  }

  const discount = offer.discount;

  return (
    <div style={{
      minHeight: '100vh', background: theme.inkDeep, color: theme.white,
      fontFamily: theme.font.body, display: 'flex', alignItems: 'center',
      justifyContent: 'center', padding: 24, textAlign: 'center',
    }}>
      <div style={{ maxWidth: 520 }}>
        <div style={{
          display: 'inline-flex', alignItems: 'center', gap: 9, color: theme.gold,
          fontSize: 10, letterSpacing: '0.26em', textTransform: 'uppercase', marginBottom: 20,
        }}>
          <Sparkles size={12} /> {t('referral.eyebrow')}
        </div>

        <h1 style={{
          fontFamily: theme.font.display, fontSize: 'clamp(30px, 6vw, 48px)',
          fontWeight: 300, lineHeight: 1.1, margin: '0 0 20px',
        }}>
          {t('referral.sentBy', { partner: offer.partner_name })}
        </h1>

        {discount ? (
          <p style={{ color: theme.alpha.white(0.72), fontSize: 16, lineHeight: 1.7, margin: '0 0 32px' }}>
            {discount.type === 'percentage'
              ? t('referral.offerPercent', { value: parseFloat(discount.value) })
              : t('referral.offerFixed', { value: parseFloat(discount.value).toFixed(2) })}
            {' '}
            {/* The code is shown, not just applied silently: a visitor who
                switches device, or who lands here with a blocked localStorage,
                still has something they can type at checkout. */}
            {t('referral.codeIs', { code })}
          </p>
        ) : (
          <p style={{ color: theme.alpha.white(0.72), fontSize: 16, lineHeight: 1.7, margin: '0 0 32px' }}>
            {t('referral.noDiscount')}
          </p>
        )}

        <button
          onClick={() => navigate('/browse')}
          style={{
            display: 'inline-flex', alignItems: 'center', gap: 10,
            background: theme.gold, color: theme.inkDeep, border: 'none',
            padding: '16px 34px', borderRadius: 2, fontSize: 13,
            letterSpacing: '0.14em', textTransform: 'uppercase',
            cursor: 'pointer', fontFamily: theme.font.body, fontWeight: 500,
          }}>
          {t('referral.cta')} <ArrowRight size={15} />
        </button>
      </div>
    </div>
  );
}
